/**
 * VerifiedSellerBadge — the sharp editorial trust mark for KYC+GST-verified
 * brands. Sits inline on product cards, list rows, and PDPs. Never rendered
 * for `verified_brand=false` products.
 *
 * Three sizes for different surfaces:
 *   xs  → 16px badge on tight product cards (small pill icon only)
 *   sm  → 22px badge with "Verified" text for list rows
 *   md  → 32px medallion + label for PDPs
 */
import React from "react";
import { BadgeCheck } from "lucide-react";

interface Props {
  verified?: boolean | null;
  size?: "xs" | "sm" | "md";
  showLabel?: boolean;
  className?: string;
  testIdSuffix?: string;
  /** Tooltip text shown on hover */
  tooltip?: string;
}

const SIZE_CLS = {
  xs: { icon: "h-3 w-3", pill: "h-4 px-1 gap-0.5 text-[9px]" },
  sm: { icon: "h-3.5 w-3.5", pill: "h-5 px-1.5 gap-1 text-[10px]" },
  md: { icon: "h-4 w-4", pill: "h-7 px-2.5 gap-1.5 text-xs" },
};

export const VerifiedSellerBadge: React.FC<Props> = ({
  verified,
  size = "sm",
  showLabel = true,
  className = "",
  testIdSuffix,
  tooltip = "Verified brand · KYC + GST completed",
}) => {
  if (!verified) return null;
  const cls = SIZE_CLS[size];
  const suffix = testIdSuffix ? `-${testIdSuffix}` : "";
  return (
    <span
      data-testid={`verified-seller-badge${suffix}`}
      title={tooltip}
      className={`inline-flex items-center rounded-full font-bold uppercase tracking-widest bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500/30 ${cls.pill} ${className}`}
    >
      <BadgeCheck className={`${cls.icon} text-emerald-600 dark:text-emerald-400`} strokeWidth={2.4} />
      {showLabel && <span className="leading-none">Verified</span>}
    </span>
  );
};

export default VerifiedSellerBadge;
